import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';
import clsx from 'clsx';
import _ from 'lodash';
import {Button, Checkbox, Grid, GridList, TextField, Theme, Typography, useMediaQuery, useTheme} from '@material-ui/core';
import {makeStyles} from '@material-ui/styles';
import {BookingData, GuestData, newGuestData, RoomData} from '../helperFunctions';
import {Group} from '../../../graphql/groups.datatypes';
import ContactUs from '../../../components/ContactUs';
import {T, useT} from 'react-polyglot-hooks';
import GuestInfo from './GuestInfo';
import GuestInfoPlaceholder from './GuestInfoPlaceholder';
import GuestListControl from './GuestListControl';
import HelpTooltip from './HelpTooltip';

const MAX_TRAVELLER_PLACEHOLDERS = 4;

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        padding: theme.spacing(0),
        paddingBottom: theme.spacing(3),
    },
    padBottom: {
        paddingBottom: theme.spacing(3),
    },
    subheading: {
        fontFamily: 'Nunito',
        fontWeight: 'bold',
        fontSize: 24,
    },
    subheadingTopMargin: {
        marginTop: theme.spacing(4)
    },
    body: {
        fontFamily: 'Nunito',
        fontSize: 18
    },
    textField: {
        width: '100%',
        backgroundColor: theme.palette.white,

        '& .MuiInputBase-inputMultiline': {
            minHeight: 150
        }
    },
    removeRoom: {
        borderWidth: 2,
        borderRadius: 0,
        textTransform: 'none',
        '&:hover, &.Mui-focusVisible': {
            borderWidth: 2
        },
    },
    warningContainer: {
        marginTop: theme.spacing(4),
        paddingBottom: 0
    },
    warningText: {
        color: theme.palette.error.main,
        fontSize: 16
    },
    checkboxContainer: {
        marginLeft: -9,
        color: theme.palette.secondary.main,
        maxWidth: 42,
        flex: 1,
    },
    checkboxLabel: {
        flex: 1,
    },
    scrollSection: {
        flexWrap: 'nowrap',
        transform: 'translateZ(0)',
        overflowX: 'scroll',
        width: '100vw',
        paddingBottom: theme.spacing(2),
    },
    scrollSectionItem: {
        paddingRight: theme.spacing(2)
    }
}));

interface Props {
    checkCapacityAvailable: (numGuests: number, checkNewRoom?: boolean) => boolean;
    getAvailableMaxCapacity: () => number;
    getCurrentMaxCapacity: () => number;
    group: Group;
    roomData: RoomData;
    addRoom: () => void;
    removeRoom: (roomData: RoomData) => void;
    setRoomData: (roomData: RoomData) => void;
    billingPartyId: string,
    bookingData: BookingData;
    containerRef: React.RefObject<HTMLDivElement | null>;
    readonly?: boolean;
}

const RoomParty = ({
    checkCapacityAvailable,
    getAvailableMaxCapacity,
    getCurrentMaxCapacity,
    group,
    roomData,
    addRoom,
    removeRoom,
    setRoomData,
    billingPartyId,
    bookingData,
    containerRef,
    readonly
}: Props) => {
    const classes = useStyles();
    const t = useT();
    const theme = useTheme();
    const [open, setOpen] = React.useState(false);
    const handleClickOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);
    const [containerMargin, setContainerMargin] = useState(0);
    const firstRoomRef = useRef<HTMLDivElement | null>(null);

    const guestsData = roomData.guestsData;
    const errors = [];
    const scrollSectionInlineStyles = {
        marginLeft: -containerMargin,
        paddingLeft: containerMargin,
        scrollLeft: 0,
    };
    const isCurrentRoomAvailable = checkCapacityAvailable(1);
    const isNextRoomAvailable = checkCapacityAvailable(1, true);
    //const roomResource = group && group.hotel && group.hotel.roomResources && group.hotel.roomResources[0];
    const maxCapacityAvailable = getAvailableMaxCapacity();
    const currentMaxCapacity = getCurrentMaxCapacity();
    //const maxOccupancy = Math.min(roomResource && roomResource.guest_capacity || 1);
    const maxCapacity = Math.max(currentMaxCapacity, maxCapacityAvailable);
    const placeholderCount = Math.max(Math.min(maxCapacity, MAX_TRAVELLER_PLACEHOLDERS) - guestsData.length, 0);
    const isMaxOccupancy = !checkCapacityAvailable(guestsData.length + 1);
    const roomNumber = bookingData.roomsData.indexOf(roomData);

    if (!guestsData.find(guestData => guestData.primary_occupant)) errors.push('No room leader set. Each room must have a room leader.');
    if (guestsData.filter(guestData => guestData.tour_role === -1).length > 0) errors.push('Tour Role Required. If under age 3 at time of travel, child is free and must be listed in the Special Requests box below. Age 3 and older are considered a paying passenger.');

    const addGuest = () => {
        const newGuest = newGuestData(roomData.id, billingPartyId);
        const newGuestsData = [...guestsData, newGuest];
        newGuest.roomGuestNumber = newGuestsData.length;
        const isValid = newGuestsData.find(data => !data.isValid) === undefined;
        setRoomData({...roomData, guestsData: newGuestsData, isValid});
    };

    const removeGuest = (targetData: GuestData) => {
        const filtered = guestsData.filter(guestData => guestData.id !== targetData.id);
        const newGuestsData = filtered.map((guestData, i) => ({...guestData, roomGuestNumber: i}));
        const isValid = newGuestsData.find(data => !data.isValid) === undefined;
        setRoomData({...roomData, guestsData: newGuestsData, isValid});
    };

    const setGuestData = (targetData: GuestData) => {
        const newGuestsData: GuestData[] = roomData.guestsData.map(guestData => guestData.id !== targetData.id ? guestData : targetData);
        const isValid = newGuestsData.find(data => !data.isValid) === undefined;
        setRoomData({...roomData, guestsData: newGuestsData, isValid});
    };

    useEffect(() => {
        const isValid = (roomData.guestsData.find(data => !data.isValid) === undefined) && (errors.length === 0);
        if (roomData.isValid !== isValid) {
            setRoomData({...roomData, isValid});
        }
    }, [roomData.guestsData]);

    useLayoutEffect(() => {
        const updateContainerMargin = () => {
            if (containerRef.current !== null) {
                const containerBounds = containerRef.current.getBoundingClientRect();
                let newMargin = containerBounds.left;

                if(theme.breakpoints.down('sm')){
                    newMargin += theme.spacing(3);
                }
                setContainerMargin(newMargin);
            }
        };
        updateContainerMargin();
        if(containerRef.current) containerRef.current.scrollLeft = 0;
        const resizeHandler = _.debounce(updateContainerMargin, 150);
        window.addEventListener('resize', resizeHandler);

        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    }, [containerRef.current]);

    const isMobile = useMediaQuery(theme.breakpoints.down("sm"), {
        defaultMatches: true
    });

    // useEffect(() => {
    //     if (firstRoomRef.current !== null) {
    //         firstRoomRef.current.scrollIntoView({behavior: 'smooth'});
    //     }
    // }, [firstRoomRef.current]);

    if (!isCurrentRoomAvailable) {
        return (
            <Grid className={classes.root} container spacing={1}>
                <Grid item xs={12}>
                    <Typography variant="h3" color="error"
                                className={clsx([classes.subheading, classes.padBottom])}>Room
                        Unavailable</Typography>
                    <Typography variant="body1" className={clsx([classes.body, classes.padBottom])}>
                        <T phrase="no-rooms"/><br/>
                        <Button variant="contained" onClick={handleClickOpen}>contact us</Button>
                    </Typography>
                </Grid>
                <ContactUs onClose={handleClose} open={open} groupId={group.id} heading={'Contact us'}
                        message={String(t('no-rooms'))}
                        adminMessage={`No rooms available in group ${group.id} `}/>
            </Grid>
        )
    }

    return (
        <React.Fragment>
            <Grid className={classes.root} container spacing={1}>
                <Grid item xs={12}>
                    <Typography
                        variant="h3"
                        className={clsx([classes.subheading, classes.padBottom])}
                    >
                        Room {roomNumber + 1}:
                        Traveler Information
                    </Typography>
                    <Typography variant="body1" className={clsx([classes.body, classes.padBottom])}>
                        Please enter all traveler details per room below.<br/>
                        All rooms must have a room leader.<br/>
                        Max {maxCapacity} people per room - you can add additional rooms if necessary.
                    </Typography>
                </Grid>
            </Grid>

            {!isMobile && (
                <GridList
                    className={classes.scrollSection}
                    style={scrollSectionInlineStyles}
                >
                    {guestsData.map((guestData, index) => (
                        <GuestInfo
                            roomRef={index === 0 ? firstRoomRef : undefined}
                            key={index}
                            bookingData={bookingData}
                            group={group}
                            guestData={guestData}
                            guestIndex={index}
                            roomGuestCount={guestsData.length}
                            removeGuest={removeGuest}
                            setGuestData={setGuestData}
                            readonly={readonly}
                        />
                    ))}

                    {!readonly &&
                        Array.from(new Array(placeholderCount)).map((item, index) => (
                            <GuestInfoPlaceholder
                                    key={index}
                                    group={group}
                                    guestIndex={guestsData.length + index}
                                    roomGuestCount={guestsData.length}
                                    addGuestHandler={addGuest}
                                />
                            )
                        )}

                    {!readonly && (
                        <GuestListControl
                            isFull={isMaxOccupancy}
                            maxOccupancy={maxCapacity}
                            isNextRoomAvailable={isNextRoomAvailable}
                            roomNumber={roomNumber}
                            rightSpacerWidth={containerMargin}
                            addGuestHandler={addGuest}
                            addRoomHandler={addRoom}
                        />
                    )}
                </GridList>
            )}

            {isMobile && (
                <Grid
                    style={{
                        overflowY: "scroll",
                        flexWrap: "nowrap",
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                        width: "100%",
                        justifyContent: "space-between",
                        padding: "0 16px",
                        boxSizing: "border-box"
                    }}
                >
                    {guestsData.map((guestData, index) => (
                        <GuestInfo
                            roomRef={index === 0 ? firstRoomRef : undefined}
                            key={index}
                            bookingData={bookingData}
                            group={group}
                            guestData={guestData}
                            guestIndex={index}
                            roomGuestCount={guestsData.length}
                            removeGuest={removeGuest}
                            setGuestData={setGuestData}
                            readonly={readonly}
                        />
                    ))}

                    {!readonly &&
                        Array.from(new Array(placeholderCount)).map((item, index) => (
                            <GuestInfoPlaceholder
                                    key={index}
                                    group={group}
                                    guestIndex={guestsData.length + index}
                                    roomGuestCount={guestsData.length}
                                    addGuestHandler={addGuest}
                                />
                            )
                        )}

                    {!readonly && (
                        <GuestListControl
                            isFull={isMaxOccupancy}
                            maxOccupancy={maxCapacity}
                            isNextRoomAvailable={isNextRoomAvailable}
                            roomNumber={roomNumber}
                            rightSpacerWidth={containerMargin}
                            addGuestHandler={addGuest}
                            addRoomHandler={addRoom}
                        />
                    )}
                </Grid>
            )}

            {errors.length > 0 && (
                <Grid className={clsx(classes.root, classes.warningContainer)} container spacing={1}>
                    {errors.map((error, i) => (
                        <Grid key={i} item xs={12}>
                            <Typography variant="body1" className={clsx([classes.body, classes.warningText])}>
                                {error}
                            </Typography>
                        </Grid>
                    ))}
                </Grid>
            )}

            <Grid className={classes.root} container spacing={1}>
                <Grid item xs={12}>
                    <Typography variant="h3" className={clsx([
                        classes.subheading,
                        classes.subheadingTopMargin,
                        classes.padBottom
                    ])}>
                        Room {roomNumber + 1}: Special Requests
                    </Typography>
                    <Typography variant="body1" className={clsx([classes.body, classes.padBottom])}>
                        Please note that requests cannot be guaranteed. We will do our best to accommodate any special needs you may have.
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <Grid component="label" container alignItems="center" justify="flex-start">
                        <Grid item className={clsx([classes.checkboxContainer, classes.padBottom])}>
                            <Checkbox
                                name="wheelchair"
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    if (readonly) return;
                                    setRoomData({
                                        ...roomData,
                                        wheelchair: event.target.checked
                                    })
                                }}
                                checked={roomData.wheelchair}
                                disabled={readonly}
                            />
                        </Grid>
                        <Grid item className={classes.checkboxLabel}>
                            <Typography variant="body1" className={classes.body}>
                                <strong>Wheelchair Accessible Room - </strong>
                                <span>
                                    Please list hygiene needs – Roll-In Shower or Tub With Bars. All wheelchairs/scooters will fit in a standard room.  If no special hygiene needs then you may not need an accessible room.
                                </span>
                            </Typography>
                        </Grid>
                    </Grid>

                    <Grid component="label" container alignItems="center" justify="flex-start">
                        <Grid item className={clsx([classes.checkboxContainer, classes.padBottom])}>
                            <Checkbox
                                name="firstfloor"
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    if (readonly) return;
                                    setRoomData({
                                        ...roomData,
                                        hasInfants: event.target.checked
                                    })
                                }}
                                checked={roomData.hasInfants}
                                disabled={readonly}
                            />
                        </Grid>
                        <Grid item className={classes.checkboxLabel}>
                            <Typography variant="body1" className={classes.body}>
                                <strong>Infants in room (under age 3) – </strong>
                                <span>
                                    Hotel requires full name and date of birth (please add details in the box below - Example – John Doe 1/1/21)
                                </span>
                            </Typography>
                        </Grid>
                    </Grid>

                    <Grid container>
                        <Grid item xs={12}>
                            <Typography variant="body1" className={classes.subheadingTopMargin}>
                                Please indicate any room requests for ONSITE at the hotel (Ex-adjoining rooms).  Please do NOT list any other questions or concerns as we are not notified when a comment is placed in this field.  If you have any other requests or comments please send us an email.
                            </Typography>
                            <TextField
                                multiline
                                rows="4"
                                className={classes.textField}
                                margin="normal"
                                variant="outlined"
                                name="notes"
                                placeholder="Room Requests for ONSITE at hotel (e.g. adjoining rooms)"
                                onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
                                    if (readonly) return;
                                    setRoomData({
                                        ...roomData,
                                        notes: event.target.value
                                    })
                                }}
                                value={roomData.notes}
                                disabled={readonly}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    {roomNumber > 0 && (
                        <Button
                            size="large"
                            color="primary"
                            variant="contained"
                            onClick={() => removeRoom(roomData)}
                            className={classes.removeRoom}
                            disabled={readonly}
                        >
                            - Remove This Room
                        </Button>
                    )}
                </Grid>
            </Grid>
        </React.Fragment>
    )
}

export default RoomParty;
